import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Banner,
  BlockStack,
  Button,
  Checkbox,
  InlineStack,
  Modal,
  Spinner,
  Text,
  TextField,
} from '@shopify/polaris';
import {
  ChevronLeftMinor,
  ChevronRightMinor,
} from '@shopify/polaris-icons';
import { generationImportService } from '../../services/generationImportService';
import { generationScenesService } from '../../services/generationScenesService';
import { generationStoriesService } from '../../services/generationStoriesService';
import TextScenePreview from '../text/TextScenePreview';
import TextStyleEditor from '../text/TextStyleEditor';
import {
  buildTextStylePayload,
  normalizeTextStyle,
  resolveEffectiveTextStyle,
} from '../../utils/textStyleLayout';

const draftFromScene = (scene) => {
  const override = scene?.text_style_override || null;
  return {
    text: String(scene?.text || ''),
    use_default: !override || override.use_default !== false,
    style: normalizeTextStyle(override || {}),
    ...(scene?.type === 'image' ? {
      text_overlay_enabled: scene.text_overlay_enabled === true,
    } : {}),
  };
};

const scenePayloadFromDraft = (draft) => ({
  text: String(draft?.text || ''),
  text_style_override: draft?.use_default
    ? { use_default: true }
    : buildTextStylePayload(draft?.style, { useDefault: false }),
  ...(draft?.text_overlay_enabled !== undefined ? {
    text_overlay_enabled: draft.text_overlay_enabled === true,
  } : {}),
});

const payloadFingerprint = (payload) => JSON.stringify(payload);

const EditStoryTextScenesModal = ({
  isOpen,
  onClose,
  selectedStory,
}) => {
  const [scenes, setScenes] = useState([]);
  const [drafts, setDrafts] = useState({});
  const draftsRef = useRef({});
  const [originals, setOriginals] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [fonts, setFonts] = useState([]);
  const [isLoadingFonts, setIsLoadingFonts] = useState(false);
  const [previewKidName, setPreviewKidName] = useState('Kid Name');
  const [generatedPreviews, setGeneratedPreviews] = useState({});
  const generatedPreviewsRef = useRef({});
  const previewInFlightRef = useRef(new Set());
  const previewGenerationRef = useRef(0);
  const [previewingSceneIds, setPreviewingSceneIds] = useState({});
  const [saveStatuses, setSaveStatuses] = useState({});
  const [error, setError] = useState('');
  const [isSavingAll, setIsSavingAll] = useState(false);

  const fontOptions = useMemo(() => [
    { label: 'Language fallback font', value: '' },
    ...fonts.map((font) => ({
      label: `${font.label || font.family || font.key} (${font.key})`,
      value: font.key || '',
    })),
  ], [fonts]);

  const currentScene = scenes[currentIndex] || null;
  const currentDraft = currentScene ? drafts[currentScene._id] : null;
  const storyDefaultForPreview = selectedStory?.text_default_style || null;
  const currentEffectiveStyle = currentDraft
    ? resolveEffectiveTextStyle(
      storyDefaultForPreview,
      currentDraft.use_default ? { use_default: true } : {
        ...currentDraft.style,
        use_default: false,
      }
    )
    : null;

  const isSceneDirty = useCallback((sceneId) => {
    if (!sceneId || !drafts[sceneId]) return false;
    return payloadFingerprint(scenePayloadFromDraft(drafts[sceneId])) !== originals[sceneId];
  }, [drafts, originals]);

  const clearGeneratedPreviews = useCallback(() => {
    previewGenerationRef.current += 1;
    setGeneratedPreviews((previous) => {
      Object.values(previous).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
      return {};
    });
  }, []);

  useEffect(() => {
    if (!isOpen || !selectedStory?._id) return undefined;
    let mounted = true;
    setIsLoading(true);
    setError('');
    setCurrentIndex(0);
    clearGeneratedPreviews();

    Promise.all([
      generationStoriesService.getTextScenes(selectedStory._id),
      generationImportService.getFonts({ activeOnly: true }),
    ]).then(([sceneData, fontResult]) => {
      if (!mounted) return;
      const textScenes = (Array.isArray(sceneData) ? sceneData : [])
        .filter((scene) => (
          scene.type === 'text' ||
          (scene.type === 'image' && scene.text_overlay_enabled === true)
        ))
        .sort((left, right) => Number(left.order || 0) - Number(right.order || 0));
      const nextDrafts = {};
      const nextOriginals = {};
      textScenes.forEach((scene) => {
        const draft = draftFromScene(scene);
        nextDrafts[scene._id] = draft;
        nextOriginals[scene._id] = payloadFingerprint(scenePayloadFromDraft(draft));
      });
      setScenes(textScenes);
      setDrafts(nextDrafts);
      setOriginals(nextOriginals);
      setFonts(Array.isArray(fontResult?.data) ? fontResult.data : []);
    }).catch((loadError) => {
      if (mounted) setError(loadError.message || 'Failed to load text scenes');
    }).finally(() => {
      if (mounted) {
        setIsLoading(false);
        setIsLoadingFonts(false);
      }
    });

    setIsLoadingFonts(true);
    return () => {
      mounted = false;
    };
  }, [clearGeneratedPreviews, isOpen, selectedStory]);

  useEffect(() => {
    generatedPreviewsRef.current = generatedPreviews;
  }, [generatedPreviews]);

  useEffect(() => {
    draftsRef.current = drafts;
  }, [drafts]);

  useEffect(() => () => {
    Object.values(generatedPreviewsRef.current).forEach((url) => {
      if (url) URL.revokeObjectURL(url);
    });
  }, []);

  useEffect(() => {
    document.body.classList.toggle('edit-story-text-scenes-modal-open', isOpen);
    return () => {
      document.body.classList.remove('edit-story-text-scenes-modal-open');
    };
  }, [isOpen]);

  const updateCurrentDraft = (patch) => {
    if (!currentScene) return;
    clearGeneratedPreviews();
    setDrafts((previous) => ({
      ...previous,
      [currentScene._id]: {
        ...previous[currentScene._id],
        ...patch,
      },
    }));
    setSaveStatuses((previous) => ({ ...previous, [currentScene._id]: 'unsaved' }));
    setError('');
  };

  const saveScene = useCallback(async (sceneId) => {
    if (!sceneId || !drafts[sceneId] || !isSceneDirty(sceneId)) return true;
    const draft = drafts[sceneId];
    if (!String(draft.text || '').trim()) {
      setError('Scene text cannot be empty.');
      return false;
    }

    setSaveStatuses((previous) => ({ ...previous, [sceneId]: 'saving' }));
    setError('');
    try {
      const payload = scenePayloadFromDraft(draft);
      const submittedFingerprint = payloadFingerprint(payload);
      await generationStoriesService.updateTextScenes(selectedStory._id, {
        scenes: [{ scene_id: sceneId, ...payload }],
      });
      const currentFingerprint = payloadFingerprint(
        scenePayloadFromDraft(draftsRef.current[sceneId])
      );
      if (currentFingerprint !== submittedFingerprint) {
        setSaveStatuses((previous) => ({ ...previous, [sceneId]: 'unsaved' }));
        setError('The scene changed while it was saving. Save it again before navigating.');
        return false;
      }
      setOriginals((previous) => ({
        ...previous,
        [sceneId]: submittedFingerprint,
      }));
      setSaveStatuses((previous) => ({ ...previous, [sceneId]: 'saved' }));
      return true;
    } catch (saveError) {
      setSaveStatuses((previous) => ({ ...previous, [sceneId]: 'error' }));
      setError(saveError.message || 'Failed to save scene');
      return false;
    }
  }, [drafts, isSceneDirty, selectedStory]);

  const navigateToScene = async (nextIndex) => {
    if (!currentScene || nextIndex === currentIndex || nextIndex < 0 || nextIndex >= scenes.length) return;
    const saved = await saveScene(currentScene._id);
    if (saved) {
      setCurrentIndex(nextIndex);
      setError('');
    }
  };

  const saveAll = async () => {
    const dirtyScenes = scenes.filter((scene) => isSceneDirty(scene._id));
    if (dirtyScenes.length === 0) return true;
    const blankScene = dirtyScenes.find(
      (scene) => !String(drafts[scene._id]?.text || '').trim()
    );
    if (blankScene) {
      setError(`Scene ${blankScene.order} text cannot be empty.`);
      return false;
    }

    setIsSavingAll(true);
    setError('');
    setSaveStatuses((previous) => {
      const next = { ...previous };
      dirtyScenes.forEach((scene) => {
        next[scene._id] = 'saving';
      });
      return next;
    });
    try {
      const submittedFingerprints = {};
      const payload = {
        scenes: dirtyScenes.map((scene) => {
          const scenePayload = scenePayloadFromDraft(drafts[scene._id]);
          submittedFingerprints[scene._id] = payloadFingerprint(scenePayload);
          return { scene_id: scene._id, ...scenePayload };
        }),
      };
      await generationStoriesService.updateTextScenes(selectedStory._id, payload);

      setOriginals((previous) => {
        const next = { ...previous };
        dirtyScenes.forEach((scene) => {
          next[scene._id] = submittedFingerprints[scene._id];
        });
        return next;
      });
      const hasNewerEdits = dirtyScenes.some((scene) => {
        const currentFingerprint = payloadFingerprint(
          scenePayloadFromDraft(draftsRef.current[scene._id])
        );
        return currentFingerprint !== submittedFingerprints[scene._id];
      });
      setSaveStatuses((previous) => {
        const next = { ...previous };
        dirtyScenes.forEach((scene) => {
          const currentFingerprint = payloadFingerprint(
            scenePayloadFromDraft(draftsRef.current[scene._id])
          );
          const isCurrent = currentFingerprint === submittedFingerprints[scene._id];
          next[scene._id] = isCurrent ? 'saved' : 'unsaved';
        });
        return next;
      });
      if (hasNewerEdits) {
        setError('Some scenes changed while saving. Save them again before closing.');
        return false;
      }
      return true;
    } catch (saveError) {
      setSaveStatuses((previous) => {
        const next = { ...previous };
        dirtyScenes.forEach((scene) => {
          next[scene._id] = 'error';
        });
        return next;
      });
      setError(saveError.message || 'Failed to save story text scenes');
      return false;
    } finally {
      setIsSavingAll(false);
    }
  };

  const generatePreview = useCallback(async (scene, draft) => {
    if (!scene || !draft || !selectedStory?._id) return;
    const sceneId = scene._id;
    if (previewInFlightRef.current.has(sceneId)) return;
    previewInFlightRef.current.add(sceneId);
    const previewGeneration = previewGenerationRef.current;
    const effectiveOverride = draft.use_default && storyDefaultForPreview
      ? buildTextStylePayload(storyDefaultForPreview, { useDefault: false })
      : scenePayloadFromDraft(draft).text_style_override;

    setPreviewingSceneIds((previous) => ({ ...previous, [sceneId]: true }));
    setError('');
    try {
      const blob = await generationScenesService.renderTextScenePreview({
        story_id: selectedStory._id,
        scene_id: sceneId,
        order: scene.order,
        scene_url: scene.scene_url,
        scene_url_2: scene.scene_url_2 || scene.scene_url,
        text: draft.text,
        kid_name: previewKidName || 'Kid Name',
        source: 'online',
        text_style_override: effectiveOverride,
      });
      if (previewGeneration !== previewGenerationRef.current) return;
      const objectUrl = URL.createObjectURL(blob);
      setGeneratedPreviews((previous) => {
        if (previous[sceneId]) URL.revokeObjectURL(previous[sceneId]);
        return { ...previous, [sceneId]: objectUrl };
      });
    } catch (previewError) {
      setError(previewError.message || 'Failed to generate server preview');
    } finally {
      previewInFlightRef.current.delete(sceneId);
      setPreviewingSceneIds((previous) => ({ ...previous, [sceneId]: false }));
    }
  }, [
    previewKidName,
    selectedStory,
    storyDefaultForPreview,
  ]);

  const handleClose = async () => {
    if (isSavingAll) return;
    const hasUnsavedChanges = scenes.some((scene) => isSceneDirty(scene._id));
    if (hasUnsavedChanges && !(await saveAll())) {
      return;
    }
    clearGeneratedPreviews();
    onClose();
  };

  const statusText = currentScene
    ? {
      saving: 'Saving…',
      saved: 'Saved',
      unsaved: 'Unsaved',
      error: 'Save failed',
    }[saveStatuses[currentScene._id]] || (isSceneDirty(currentScene._id) ? 'Unsaved' : 'Saved')
    : '';

  return (
    <>
      <style>{`
        .edit-story-text-scenes-modal-open .Polaris-Modal-Dialog__Modal {
          width: min(96vw, 90rem) !important;
          max-width: min(96vw, 90rem) !important;
        }

        .edit-story-text-scenes-workspace {
          display: grid;
          grid-template-columns: minmax(0, 1.15fr) minmax(360px, 0.85fr);
          gap: 20px;
          align-items: start;
        }

        .edit-story-text-scenes-preview {
          min-width: 0;
          position: sticky;
          top: 0;
        }

        @media (max-width: 59.99rem) {
          .edit-story-text-scenes-workspace {
            grid-template-columns: minmax(0, 1fr);
          }

          .edit-story-text-scenes-preview {
            position: static;
          }
        }
      `}</style>
      <Modal
        open={isOpen}
        onClose={handleClose}
        title={selectedStory ? `${selectedStory.title} - Text Scenes` : 'Text Scenes'}
        size="large"
        primaryAction={{
          content: 'Save all',
          onAction: saveAll,
          loading: isSavingAll,
          disabled: isLoading,
        }}
        secondaryActions={[
          {
            content: 'Close',
            onAction: handleClose,
            disabled: isSavingAll,
          },
        ]}
      >
        <div style={{ padding: 16 }}>
          <BlockStack gap="400">
            {error ? <Banner tone="critical">{error}</Banner> : null}

            <div style={{ maxWidth: 320 }}>
              <TextField
                label="Preview kid name"
                value={previewKidName}
                onChange={(kidName) => {
                  clearGeneratedPreviews();
                  setPreviewKidName(kidName);
                }}
                autoComplete="off"
              />
            </div>

            {isLoading ? (
              <BlockStack gap="300" align="center">
                <Spinner size="large" />
                <Text tone="subdued">Loading text scenes…</Text>
              </BlockStack>
            ) : currentScene && currentDraft ? (
              <>
              <InlineStack align="space-between" blockAlign="center">
                <InlineStack gap="200" blockAlign="center">
                  <Button
                    icon={ChevronLeftMinor}
                    accessibilityLabel="Previous scene"
                    onClick={() => navigateToScene(currentIndex - 1)}
                    disabled={currentIndex === 0 || saveStatuses[currentScene._id] === 'saving' || isSavingAll}
                  />
                  <Text variant="headingMd">
                    {`Scene ${currentScene.order} (${currentIndex + 1} of ${scenes.length})`}
                  </Text>
                  <Button
                    icon={ChevronRightMinor}
                    accessibilityLabel="Next scene"
                    onClick={() => navigateToScene(currentIndex + 1)}
                    disabled={currentIndex === scenes.length - 1 || saveStatuses[currentScene._id] === 'saving' || isSavingAll}
                  />
                </InlineStack>
                <Text
                  variant="bodySm"
                  tone={statusText === 'Save failed' ? 'critical' : 'subdued'}
                >
                  {statusText}
                </Text>
              </InlineStack>

              <div className="edit-story-text-scenes-workspace">
                <div className="edit-story-text-scenes-preview">
                  <TextScenePreview
                    imageUrl={currentScene.scene_url || currentScene.scene_url_2}
                    text={currentDraft.text}
                    kidName={previewKidName}
                    language={selectedStory?.language}
                    order={currentScene.order}
                    source="online"
                    style={currentEffectiveStyle}
                    fonts={fonts}
                    generatedPreviewUrl={generatedPreviews[currentScene._id]}
                  />
                </div>

                <div style={{ border: '1px solid #e3e3e3', borderRadius: 8, padding: 14 }}>
                  <BlockStack gap="300">
                    <TextField
                      label="Scene text"
                      value={currentDraft.text}
                      onChange={(text) => updateCurrentDraft({ text })}
                      multiline={5}
                      autoComplete="off"
                      disabled={saveStatuses[currentScene._id] === 'saving' || isSavingAll}
                    />
                    <Checkbox
                      label="Use story default text style"
                      checked={currentDraft.use_default}
                      onChange={(checked) => updateCurrentDraft({ use_default: checked })}
                      disabled={saveStatuses[currentScene._id] === 'saving' || isSavingAll}
                    />
                    {currentDraft.use_default ? (
                      <Text as="p" variant="bodySm" tone="subdued">
                        This scene inherits the effective story style shown in the preview.
                      </Text>
                    ) : (
                      <TextStyleEditor
                        value={currentDraft.style}
                        onChange={(style) => updateCurrentDraft({ style })}
                        fontOptions={fontOptions}
                        isLoadingFonts={isLoadingFonts}
                        disabled={saveStatuses[currentScene._id] === 'saving' || isSavingAll}
                      />
                    )}
                    <InlineStack gap="200">
                      <Button
                        variant="primary"
                        onClick={() => saveScene(currentScene._id)}
                        loading={saveStatuses[currentScene._id] === 'saving'}
                        disabled={!isSceneDirty(currentScene._id) || isSavingAll}
                      >
                        Save scene
                      </Button>
                      <Button
                        onClick={() => generatePreview(currentScene, currentDraft)}
                        loading={Boolean(previewingSceneIds[currentScene._id])}
                        disabled={isSavingAll}
                      >
                        Test generate
                      </Button>
                    </InlineStack>
                  </BlockStack>
                </div>
              </div>
              </>
            ) : (
              <Text tone="subdued">No text scenes were found for this story.</Text>
            )}
          </BlockStack>
        </div>
      </Modal>
    </>
  );
};

export default EditStoryTextScenesModal;
